// The doors into the learner site and the one room that is about the learner
// themselves: signing in (by phone number, or by username), registering, and
// Settings — profile, language and password.
//
// It also speaks for the page that stands in front of the whole site when a
// learner signed in with a password somebody else chose, until they choose
// their own (App.tsx, pages/ChoosePassword.tsx).
//
// Two server codes are new with these screens and so are translated here
// rather than in errors.ts: phone_taken (registering a number that already has
// an account with a password) and invalid_locale (saving a language the server
// does not know).

import { defineMessages, plural } from './index'

/** "at least 8 characters" needs the genitive plural in Russian. */
const ruChars = (n: number | string) =>
  plural('ru', Number(n), { one: '{n} символа', few: '{n} символов', many: '{n} символов', other: '{n} символа' })

export default defineMessages(
  {
    // ------------------------------------------------------------- sign in --
    signInTitle: 'Kirish',
    signInLead: 'Telefon raqamingiz va parolingiz bilan kiring.',
    signInLeadUsername: 'Loginingiz va parolingiz bilan kiring.',
    phoneLabel: 'Telefon raqami',
    usernameLabel: 'Login',
    usernamePlaceholder: 'masalan: dilnoza.k',
    passwordLabel: 'Parol',
    showPassword: 'Parolni ko‘rsatish',
    hidePassword: 'Parolni yashirish',
    useUsername: 'Login bilan kirish',
    usePhone: 'Telefon raqami bilan kirish',
    firstTimeTitle: 'Birinchi marta kiryapsizmi?',
    firstTimeBody:
      'Administratoringiz sizga parol bergan bo‘lsa, telefon raqamingiz va shu parol bilan kiring — ro‘yxatdan o‘tish shart emas. Kirganingizdan keyin o‘zingiz uchun yangi parol tanlaysiz.',
    signIn: 'Kirish',
    signingIn: 'Tekshirilmoqda…',
    noAccount: 'Hisobingiz yo‘qmi?',
    forgotTitle: 'Parolni unutdingizmi?',
    forgotBody:
      'Mentoringiz yoki o‘quv markazi administratoriga yozing. Ular sizga yangi vaqtinchalik parol beradi. Telefon raqamingiz va shu parol bilan kiring — tizim darhol o‘zingizga yangi parol tanlashni so‘raydi.',
    register: 'Ro‘yxatdan o‘tish',
    enterPhone: 'Telefon raqamingizni kiriting.',
    phoneIncomplete: 'Raqamni to‘liq kiriting: +998 dan keyin 9 ta raqam.',
    enterUsername: 'Loginingizni kiriting.',
    usernameShape: 'Login {min}–{max} ta belgidan iborat: lotin harflari, raqamlar va . _ - belgilari.',
    enterPassword: 'Parolni kiriting.',
    badPhoneCredentials: 'Telefon raqami yoki parol noto‘g‘ri.',
    badUsernameCredentials: 'Login yoki parol noto‘g‘ri.',
    lockedCountdown: 'Juda ko‘p noto‘g‘ri urinish bo‘ldi, hisob vaqtincha yopildi. {time} dan keyin qayta urinib ko‘ring.',
    lockedLater: 'Juda ko‘p noto‘g‘ri urinish bo‘ldi, hisob vaqtincha yopildi. Birozdan keyin qayta urinib ko‘ring.',
    // Said under either of the two above, on the sign-in page only. A lock
    // turns away every browser but one that holds a device of this account
    // (its device cookie, internal/auth/device.go), and that one gets a single
    // try while the lock lasts. A browser earns one by using a password of the
    // person's own on this site — signing in with it, registering, or choosing
    // it on the "choose your own password" page; a sign-in with a password
    // somebody else issued earns nothing — and keeps it for 90 days from its
    // last sign-in unless, meanwhile: somebody else signs in on it (theirs
    // replaces it); the password is changed in another browser (a change
    // forgets every device but its own); staff or the operator reset the
    // password (which forgets them all); or five other browsers sign in to the
    // account more recently (it keeps five). This may be such a browser, or it
    // may not; the note gives the condition as a person can check it — the
    // cap goes under "recently" — and says what to do otherwise.
    lockedKnownDevice:
      'Agar yaqinda aynan shu brauzerda, shu saytda bu hisobga o‘zingiz tanlagan parol bilan kirgan bo‘lsangiz, shundan beri parol boshqa joyda o‘zgartirilmagan yoki tiklanmagan va bu brauzerda boshqa hech kim kirmagan bo‘lsa, blok davom etayotgan paytda bu brauzerga bitta urinish beriladi — parolni diqqat bilan kiriting. Aks holda blok tugashini kuting.',
    suspended: 'Bu hisob to‘xtatilgan. Administratoringizga murojaat qiling.',
    // Shown on the sign-in page of a device whose session ended because the
    // learner signed in on another one: the platform keeps a learner to one
    // device at a time (the server's session_replaced). If that was not them,
    // somebody else has their password, and changing it is the remedy.
    sessionReplaced:
      'Hisobingizga boshqa qurilmadan kirildi, shuning uchun bu qurilmadagi seans yakunlandi: bir vaqtning o‘zida faqat bitta qurilmada o‘qish mumkin. Davom etish uchun qaytadan kiring. Boshqa qurilmadan siz kirmagan bo‘lsangiz, kirganingizdan keyin parolingizni almashtiring.',

    // ------------------------------------------------------------ register --
    registerTitle: 'Ro‘yxatdan o‘tish',
    registerLead: 'Hisob ochish uchun quyidagilarni to‘ldiring. Keyin telefon raqamingiz va parolingiz bilan kirasiz.',
    issuedPasswordTitle: 'Administrator sizga parol berganmi?',
    issuedPasswordBody: 'Unda ro‘yxatdan o‘tmang: kirish sahifasida telefon raqamingiz va o‘sha parol bilan kiring.',
    notEnrolmentTitle: 'Hisob ochish — kursga yozilish emas',
    notEnrolmentBody:
      'Ro‘yxatdan o‘tganingizdan keyin kurslar ro‘yxati bo‘sh bo‘lishi mumkin — bu xatolik emas. Kursni o‘qituvchi yoki administrator sizni unga qo‘shgandan keyin ko‘rasiz. Telefon raqamingiz aynan shuning uchun so‘ralmoqda: sizni kursga shu raqam bo‘yicha qo‘shishadi.',
    fullNameLabel: 'Ism-familiya',
    enterFullName: 'Ism-familiyangizni kiriting.',
    fullNameTooLong: 'Ism-familiya ko‘pi bilan {max} ta belgi bo‘lishi mumkin.',
    passwordHint: 'Kamida {min} ta belgi.',
    passwordLength: 'Parol {min}–{max} ta belgidan iborat bo‘lsin.',
    confirmPasswordLabel: 'Parolni takrorlang',
    repeatPassword: 'Parolni yana bir marta kiriting.',
    passwordsDiffer: 'Parollar bir xil emas — qaytadan kiriting.',
    moreOptions: 'Qo‘shimcha',
    usernameOptionalLabel: 'Login (ixtiyoriy)',
    usernameOptionalHint:
      'Telefon raqamidan tashqari login bilan ham kirmoqchi bo‘lsangiz. {min}–{max} ta belgi: lotin harflari, raqamlar va . _ -',
    usernameIsPhone: 'Login telefon raqami bo‘lolmaydi — telefon raqamingiz bilan baribir kirasiz.',
    usernameTaken: 'Bu login band. Boshqasini tanlang.',
    createAccount: 'Hisob ochish',
    creating: 'Yuborilmoqda…',
    haveAccount: 'Hisobingiz bormi?',

    // ------------------------------------------------------------ settings --
    settingsTitle: 'Sozlamalar',
    profileTitle: 'Hisobingiz',
    usernameNotSet: 'belgilanmagan',
    profileReadOnly:
      'Bu ma’lumotlarni bu yerdan o‘zgartirib bo‘lmaydi. Xatolik bo‘lsa, o‘qituvchingizga yoki administratorga murojaat qiling.',
    languageTitle: 'Til',
    languageHint: 'Tanlovingiz hisobingizda saqlanadi: boshqa qurilmadan kirganingizda ham sayt shu tilda ochiladi.',
    languageGroup: 'Interfeys tili',
    passwordTitle: 'Parolni o‘zgartirish',
    otherDevicesTitle: 'Boshqa qurilmalar tizimdan chiqariladi',
    otherDevicesBody:
      'Parolni o‘zgartirsangiz, shu qurilmadan tashqari hamma joydagi seanslar tugatiladi: telefoningizda, boshqa kompyuterda va boshqa brauzerlarda qaytadan kirish kerak bo‘ladi. Parolingizni birov bilib qolgan bo‘lsa, uni hisobingizdan chiqaradigan narsa ham aynan shu.',
    currentPasswordLabel: 'Joriy parol',
    newPasswordLabel: 'Yangi parol',
    repeatNewPasswordLabel: 'Yangi parolni takrorlang',
    enterCurrentPassword: 'Joriy parolingizni kiriting.',
    enterNewPassword: 'Yangi parolni kiriting.',
    newPasswordLength: 'Yangi parol {min}–{max} ta belgidan iborat bo‘lsin.',
    newSameAsCurrent: 'Yangi parol eskisidan farq qilsin.',
    repeatNewPassword: 'Yangi parolni yana bir marta kiriting.',
    wrongCurrentPassword: 'Joriy parol noto‘g‘ri.',
    checkCurrentPassword: 'Joriy parolni tekshiring.',
    passwordAlreadyInUse: 'Bu parol allaqachon amalda. Boshqasini tanlang.',
    passwordChanged:
      'Parol o‘zgartirildi. Boshqa qurilmalardagi seanslar tugatildi — u yerlarda yangi parol bilan qaytadan kirasiz.',

    // ----------------------------------------------- choose your own password --
    choosePasswordTitle: 'O‘z parolingizni tanlang',
    choosePasswordLead:
      'Siz vaqtinchalik parol bilan kirdingiz. Uni sizga o‘qituvchi yoki administrator bergan, demak ular ham bu parolni biladi. Kurslaringizga o‘tishdan oldin faqat o‘zingiz biladigan yangi parol tanlang.',
    issuedPasswordLabel: 'Sizga berilgan parol',
    issuedPasswordHint: 'Hozir kirishda yozgan parolingiz.',
    enterIssuedPassword: 'Sizga berilgan parolni kiriting.',
    wrongIssuedPassword: 'Bu sizga berilgan parol emas. Uni xabarda qanday yozilgan bo‘lsa, aynan shunday kiriting.',
    rulesTitle: 'Yangi parol:',
    ruleLength: 'kamida {min} ta belgidan iborat bo‘lsin (ko‘pi bilan {max} ta)',
    ruleDifferent: 'sizga berilgan paroldan farq qilsin',
    ruleYours: 'boshqa saytlarda ishlatadigan parolingiz bo‘lmasin',
    newSameAsIssued: 'Yangi parol sizga berilgan paroldan farq qilsin.',
    choosePasswordDevices: 'Parol saqlangach, boshqa qurilmalardagi seanslaringiz tugatiladi. Shu yerda esa tizimda qolasiz.',
    // Where the chosen password will work. The page cannot tell who issued the
    // one being replaced, so the rule is stated for the case it applies to.
    choosePasswordScope:
      'Bu parolni sizga o‘qituvchi yoki administrator bergan bo‘lsa, hozir tanlaydigan parolingiz ham faqat shu saytda ishlaydi. Barcha MyMentor platformalarida ishlaydigan parolni faqat MyMentor operatori bera oladi — yangi vaqtinchalik parol sifatida; uni administratoringizdan so‘rang.',
    savePassword: 'Saqlash va davom etish',
    savingPassword: 'Saqlanmoqda…',
    laterHint: 'Hozir emasmi? Tizimdan chiqishingiz mumkin — keyingi safar kirganingizda bu sahifa yana ochiladi.',

    // -------------------------------------------------------- server codes --
    err_phone_taken:
      'Bu raqam bilan hisob allaqachon bor. Kirish sahifasida telefon raqamingiz bilan kiring yoki administratoringizga murojaat qiling.',
    err_invalid_locale: 'Bu til qo‘llab-quvvatlanmaydi.',
  },
  {
    en: {
      signInTitle: 'Sign in',
      signInLead: 'Sign in with your phone number and password.',
      signInLeadUsername: 'Sign in with your username and password.',
      phoneLabel: 'Phone number',
      usernameLabel: 'Username',
      usernamePlaceholder: 'e.g. dilnoza.k',
      passwordLabel: 'Password',
      showPassword: 'Show password',
      hidePassword: 'Hide password',
      useUsername: 'Sign in with a username instead',
      usePhone: 'Sign in with a phone number instead',
      firstTimeTitle: 'First time here?',
      firstTimeBody:
        'If your administrator gave you a password, sign in with your phone number and that password — you don’t need to register. Once you are in, you choose a new password of your own.',
      signIn: 'Sign in',
      signingIn: 'Signing in…',
      noAccount: 'No account yet?',
      forgotTitle: 'Forgot your password?',
      forgotBody:
        'Write to your mentor or the learning centre’s administrator. They will give you a new temporary password. Sign in with your phone number and that password — you will be asked to choose a new password of your own straight away.',
      register: 'Register',
      enterPhone: 'Enter your phone number.',
      phoneIncomplete: 'Enter the whole number: 9 digits after +998.',
      enterUsername: 'Enter your username.',
      usernameShape: 'A username is {min}–{max} characters: Latin letters, digits and . _ -',
      enterPassword: 'Enter your password.',
      badPhoneCredentials: 'Incorrect phone number or password.',
      badUsernameCredentials: 'Incorrect username or password.',
      lockedCountdown: 'Too many failed attempts, so the account is locked for now. Try again in {time}.',
      lockedLater: 'Too many failed attempts, so the account is locked for now. Try again later.',
      lockedKnownDevice:
        'If you recently signed in to this account in this browser, on this site, with a password you chose yourself, and since then the password has not been changed or reset elsewhere and nobody else has signed in here, this browser gets one attempt while the lock lasts — type the password carefully. Otherwise, wait until the lock is over.',
      suspended: 'This account has been suspended. Please contact your administrator.',
      sessionReplaced:
        'Your account was signed in on another device, so the session on this one has ended: you can study on only one device at a time. Sign in again to continue. If it wasn’t you who signed in on the other device, change your password once you’re in.',

      registerTitle: 'Create an account',
      registerLead: 'Fill in the form to create an account. After that you sign in with your phone number and password.',
      issuedPasswordTitle: 'Did your administrator give you a password?',
      issuedPasswordBody: 'Then don’t register: sign in on the sign-in page with your phone number and that password.',
      notEnrolmentTitle: 'An account is not a course enrolment',
      notEnrolmentBody:
        'Your list of courses may be empty after you register — that is not an error. You will see a course once a teacher or administrator adds you to it. That is exactly why we ask for your phone number: you are added to courses by that number.',
      fullNameLabel: 'Full name',
      enterFullName: 'Enter your full name.',
      fullNameTooLong: 'A name can be at most {max} characters.',
      passwordHint: 'At least {min} characters.',
      passwordLength: 'A password must be {min}–{max} characters long.',
      confirmPasswordLabel: 'Repeat the password',
      repeatPassword: 'Type the password once more.',
      passwordsDiffer: 'The passwords don’t match — please type them again.',
      moreOptions: 'More options',
      usernameOptionalLabel: 'Username (optional)',
      usernameOptionalHint:
        'If you also want to sign in with a username as well as your phone number. {min}–{max} characters: Latin letters, digits and . _ -',
      usernameIsPhone: 'A username can’t be a phone number — you sign in with your phone number anyway.',
      usernameTaken: 'That username is taken. Please choose another.',
      createAccount: 'Create account',
      creating: 'Creating…',
      haveAccount: 'Already have an account?',

      settingsTitle: 'Settings',
      profileTitle: 'Your account',
      usernameNotSet: 'not set',
      profileReadOnly: 'These details can’t be changed here. If something is wrong, contact your teacher or an administrator.',
      languageTitle: 'Language',
      languageHint: 'Your choice is saved to your account, so the site opens in this language on your other devices too.',
      languageGroup: 'Interface language',
      passwordTitle: 'Change password',
      otherDevicesTitle: 'Other devices will be signed out',
      otherDevicesBody:
        'When you change your password, every session except this one ends: on your phone, on another computer and in other browsers you will have to sign in again. If somebody else has learned your password, this is also what shuts them out of your account.',
      currentPasswordLabel: 'Current password',
      newPasswordLabel: 'New password',
      repeatNewPasswordLabel: 'Repeat the new password',
      enterCurrentPassword: 'Enter your current password.',
      enterNewPassword: 'Enter a new password.',
      newPasswordLength: 'The new password must be {min}–{max} characters long.',
      newSameAsCurrent: 'The new password must differ from the current one.',
      repeatNewPassword: 'Type the new password once more.',
      wrongCurrentPassword: 'The current password is incorrect.',
      checkCurrentPassword: 'Check your current password.',
      passwordAlreadyInUse: 'This is already your password. Please choose a different one.',
      passwordChanged: 'Password changed. Your sessions on other devices have ended — sign in there again with the new password.',

      choosePasswordTitle: 'Choose your own password',
      choosePasswordLead:
        'You signed in with a temporary password. A teacher or administrator gave it to you, so they know it too. Before you go on to your courses, choose a new password that only you know.',
      issuedPasswordLabel: 'The password you were given',
      issuedPasswordHint: 'The one you have just signed in with.',
      enterIssuedPassword: 'Enter the password you were given.',
      wrongIssuedPassword: 'That isn’t the password you were given. Type it exactly as it was written in the message.',
      rulesTitle: 'Your new password should:',
      ruleLength: 'be at least {min} characters long (at most {max})',
      ruleDifferent: 'differ from the password you were given',
      ruleYours: 'not be a password you use on other sites',
      newSameAsIssued: 'The new password must differ from the one you were given.',
      choosePasswordDevices: 'Once it is saved, your sessions on other devices end. You stay signed in here.',
      choosePasswordScope:
        'If a teacher or administrator gave you this password, the one you choose now will also work only on this site. A password that works on every MyMentor platform can only come from the MyMentor operator, as a new temporary password — ask your administrator for one.',
      savePassword: 'Save and continue',
      savingPassword: 'Saving…',
      laterHint: 'Not now? You can sign out — this page will open again the next time you sign in.',

      err_phone_taken:
        'This number already has an account. Sign in with your phone number on the sign-in page, or ask your administrator.',
      err_invalid_locale: 'This language is not supported.',
    },
    ru: {
      signInTitle: 'Вход',
      signInLead: 'Войдите по номеру телефона и паролю.',
      signInLeadUsername: 'Войдите по логину и паролю.',
      phoneLabel: 'Номер телефона',
      usernameLabel: 'Логин',
      usernamePlaceholder: 'например: dilnoza.k',
      passwordLabel: 'Пароль',
      showPassword: 'Показать пароль',
      hidePassword: 'Скрыть пароль',
      useUsername: 'Войти по логину',
      usePhone: 'Войти по номеру телефона',
      firstTimeTitle: 'Впервые здесь?',
      firstTimeBody:
        'Если администратор выдал вам пароль, войдите по своему номеру телефона с этим паролем — регистрироваться не нужно. После входа вы придумаете свой пароль.',
      signIn: 'Войти',
      signingIn: 'Проверяем…',
      noAccount: 'Нет аккаунта?',
      forgotTitle: 'Забыли пароль?',
      forgotBody:
        'Напишите своему ментору или администратору учебного центра. Вам выдадут новый временный пароль. Войдите по номеру телефона и этому паролю — система сразу попросит выбрать собственный новый пароль.',
      register: 'Зарегистрироваться',
      enterPhone: 'Введите номер телефона.',
      phoneIncomplete: 'Введите номер полностью: 9 цифр после +998.',
      enterUsername: 'Введите логин.',
      usernameShape: 'Логин — от {min} до {max} символов: латинские буквы, цифры и . _ -',
      enterPassword: 'Введите пароль.',
      badPhoneCredentials: 'Неверный номер телефона или пароль.',
      badUsernameCredentials: 'Неверный логин или пароль.',
      lockedCountdown: 'Слишком много неудачных попыток, аккаунт временно заблокирован. Попробуйте снова через {time}.',
      lockedLater: 'Слишком много неудачных попыток, аккаунт временно заблокирован. Попробуйте позже.',
      lockedKnownDevice:
        'Если вы недавно входили в этот аккаунт в этом браузере, на этом сайте, с паролем, который выбрали сами, и с тех пор пароль не меняли и не сбрасывали в другом месте, а здесь никто другой не входил, у этого браузера есть одна попытка, пока действует блокировка, — вводите пароль внимательно. В остальных случаях дождитесь, когда блокировка закончится.',
      suspended: 'Этот аккаунт приостановлен. Обратитесь к администратору.',
      sessionReplaced:
        'В ваш аккаунт вошли с другого устройства, поэтому сеанс на этом устройстве завершён: заниматься можно только на одном устройстве одновременно. Войдите снова, чтобы продолжить. Если на другом устройстве входили не вы, после входа смените пароль.',

      registerTitle: 'Регистрация',
      registerLead: 'Заполните форму, чтобы создать аккаунт. После этого вы будете входить по номеру телефона и паролю.',
      issuedPasswordTitle: 'Администратор выдал вам пароль?',
      issuedPasswordBody: 'Тогда не регистрируйтесь: войдите на странице входа по номеру телефона с этим паролем.',
      notEnrolmentTitle: 'Аккаунт — это ещё не запись на курс',
      notEnrolmentBody:
        'После регистрации список курсов может быть пустым — это не ошибка. Курс появится, когда преподаватель или администратор добавит вас в него. Именно поэтому мы просим номер телефона: на курсы добавляют по этому номеру.',
      fullNameLabel: 'Имя и фамилия',
      enterFullName: 'Введите имя и фамилию.',
      fullNameTooLong: 'Имя может содержать не более {max} символов.',
      passwordHint: (v) => `Не менее ${ruChars(v.min ?? 0)}.`,
      passwordLength: 'Пароль должен содержать от {min} до {max} символов.',
      confirmPasswordLabel: 'Повторите пароль',
      repeatPassword: 'Введите пароль ещё раз.',
      passwordsDiffer: 'Пароли не совпадают — введите их заново.',
      moreOptions: 'Дополнительно',
      usernameOptionalLabel: 'Логин (необязательно)',
      usernameOptionalHint:
        'Если хотите входить не только по номеру телефона, но и по логину. От {min} до {max} символов: латинские буквы, цифры и . _ -',
      usernameIsPhone: 'Логин не может быть номером телефона — по номеру вы и так входите.',
      usernameTaken: 'Этот логин уже занят. Выберите другой.',
      createAccount: 'Создать аккаунт',
      creating: 'Создаём…',
      haveAccount: 'Уже есть аккаунт?',

      settingsTitle: 'Настройки',
      profileTitle: 'Ваш аккаунт',
      usernameNotSet: 'не задан',
      profileReadOnly: 'Эти данные нельзя изменить здесь. Если что-то неверно, обратитесь к преподавателю или администратору.',
      languageTitle: 'Язык',
      languageHint: 'Выбор сохраняется в аккаунте: на других устройствах сайт тоже откроется на этом языке.',
      languageGroup: 'Язык интерфейса',
      passwordTitle: 'Смена пароля',
      otherDevicesTitle: 'Другие устройства выйдут из аккаунта',
      otherDevicesBody:
        'После смены пароля завершатся все сеансы, кроме этого: на телефоне, на другом компьютере и в других браузерах придётся войти заново. Если ваш пароль узнал кто-то ещё, именно это и закроет ему доступ к аккаунту.',
      currentPasswordLabel: 'Текущий пароль',
      newPasswordLabel: 'Новый пароль',
      repeatNewPasswordLabel: 'Повторите новый пароль',
      enterCurrentPassword: 'Введите текущий пароль.',
      enterNewPassword: 'Введите новый пароль.',
      newPasswordLength: 'Новый пароль должен содержать от {min} до {max} символов.',
      newSameAsCurrent: 'Новый пароль должен отличаться от текущего.',
      repeatNewPassword: 'Введите новый пароль ещё раз.',
      wrongCurrentPassword: 'Текущий пароль неверен.',
      checkCurrentPassword: 'Проверьте текущий пароль.',
      passwordAlreadyInUse: 'Это и есть ваш текущий пароль. Выберите другой.',
      passwordChanged: 'Пароль изменён. Сеансы на других устройствах завершены — войдите там заново с новым паролем.',

      choosePasswordTitle: 'Придумайте свой пароль',
      choosePasswordLead:
        'Вы вошли с временным паролем. Его выдал преподаватель или администратор, поэтому он его тоже знает. Прежде чем перейти к курсам, придумайте новый пароль, который будете знать только вы.',
      issuedPasswordLabel: 'Выданный вам пароль',
      issuedPasswordHint: 'Тот, с которым вы только что вошли.',
      enterIssuedPassword: 'Введите выданный вам пароль.',
      wrongIssuedPassword: 'Это не тот пароль, который вам выдали. Введите его точно так, как он написан в сообщении.',
      rulesTitle: 'Новый пароль должен:',
      ruleLength: (v) => `содержать не менее ${ruChars(v.min ?? 0)} (не более {max})`,
      ruleDifferent: 'отличаться от выданного вам пароля',
      ruleYours: 'не совпадать с паролями, которые вы используете на других сайтах',
      newSameAsIssued: 'Новый пароль должен отличаться от выданного.',
      choosePasswordDevices: 'После сохранения сеансы на других устройствах завершатся. Здесь вы останетесь в системе.',
      choosePasswordScope:
        'Если этот пароль вам выдал преподаватель или администратор, пароль, который вы придумаете сейчас, тоже будет действовать только на этом сайте. Пароль, который действует на всех платформах MyMentor, может выдать только оператор MyMentor — как новый временный пароль; попросите о нём администратора.',
      savePassword: 'Сохранить и продолжить',
      savingPassword: 'Сохраняем…',
      laterHint: 'Не сейчас? Можно выйти — эта страница откроется снова при следующем входе.',

      err_phone_taken:
        'С этим номером уже есть аккаунт. Войдите по номеру телефона на странице входа или обратитесь к администратору.',
      err_invalid_locale: 'Этот язык не поддерживается.',
    },
    de: {
      signInTitle: 'Anmelden',
      signInLead: 'Melden Sie sich mit Ihrer Telefonnummer und Ihrem Passwort an.',
      signInLeadUsername: 'Melden Sie sich mit Ihrem Benutzernamen und Ihrem Passwort an.',
      phoneLabel: 'Telefonnummer',
      usernameLabel: 'Benutzername',
      usernamePlaceholder: 'z. B. dilnoza.k',
      passwordLabel: 'Passwort',
      showPassword: 'Passwort anzeigen',
      hidePassword: 'Passwort verbergen',
      useUsername: 'Stattdessen mit Benutzernamen anmelden',
      usePhone: 'Stattdessen mit Telefonnummer anmelden',
      firstTimeTitle: 'Zum ersten Mal hier?',
      firstTimeBody:
        'Wenn Ihnen Ihr Administrator ein Passwort gegeben hat, melden Sie sich mit Ihrer Telefonnummer und diesem Passwort an – eine Registrierung ist nicht nötig. Nach der Anmeldung wählen Sie ein eigenes Passwort.',
      signIn: 'Anmelden',
      signingIn: 'Wird geprüft…',
      noAccount: 'Noch kein Konto?',
      forgotTitle: 'Passwort vergessen?',
      forgotBody:
        'Schreiben Sie Ihrem Mentor oder der Verwaltung des Lernzentrums. Sie erhalten ein neues vorläufiges Passwort. Melden Sie sich mit Ihrer Telefonnummer und diesem Passwort an – danach werden Sie sofort gebeten, ein eigenes neues Passwort zu wählen.',
      register: 'Registrieren',
      enterPhone: 'Geben Sie Ihre Telefonnummer ein.',
      phoneIncomplete: 'Geben Sie die ganze Nummer ein: 9 Ziffern nach +998.',
      enterUsername: 'Geben Sie Ihren Benutzernamen ein.',
      usernameShape: 'Ein Benutzername hat {min}–{max} Zeichen: lateinische Buchstaben, Ziffern und . _ -',
      enterPassword: 'Geben Sie Ihr Passwort ein.',
      badPhoneCredentials: 'Telefonnummer oder Passwort ist falsch.',
      badUsernameCredentials: 'Benutzername oder Passwort ist falsch.',
      lockedCountdown: 'Zu viele Fehlversuche, das Konto ist vorübergehend gesperrt. Versuchen Sie es in {time} erneut.',
      lockedLater: 'Zu viele Fehlversuche, das Konto ist vorübergehend gesperrt. Versuchen Sie es später erneut.',
      lockedKnownDevice:
        'Wenn Sie sich kürzlich in diesem Browser auf dieser Website mit einem selbst gewählten Passwort bei diesem Konto angemeldet haben und seitdem weder das Passwort anderswo geändert oder zurückgesetzt wurde noch sich jemand anderes hier angemeldet hat, hat dieser Browser während der Sperre einen Versuch – geben Sie das Passwort sorgfältig ein. Andernfalls warten Sie, bis die Sperre vorbei ist.',
      suspended: 'Dieses Konto wurde deaktiviert. Wenden Sie sich an Ihren Administrator.',
      sessionReplaced:
        'Ihr Konto wurde auf einem anderen Gerät angemeldet, deshalb wurde die Sitzung auf diesem Gerät beendet: Sie können jeweils nur auf einem Gerät lernen. Melden Sie sich erneut an, um fortzufahren. Falls nicht Sie sich auf dem anderen Gerät angemeldet haben, ändern Sie nach der Anmeldung Ihr Passwort.',

      registerTitle: 'Konto erstellen',
      registerLead: 'Füllen Sie das Formular aus, um ein Konto zu erstellen. Danach melden Sie sich mit Telefonnummer und Passwort an.',
      issuedPasswordTitle: 'Hat Ihnen Ihr Administrator ein Passwort gegeben?',
      issuedPasswordBody: 'Dann registrieren Sie sich nicht: Melden Sie sich auf der Anmeldeseite mit Ihrer Telefonnummer und diesem Passwort an.',
      notEnrolmentTitle: 'Ein Konto ist noch keine Kursanmeldung',
      notEnrolmentBody:
        'Nach der Registrierung kann Ihre Kursliste leer sein – das ist kein Fehler. Einen Kurs sehen Sie, sobald eine Lehrkraft oder ein Administrator Sie hinzufügt. Genau deshalb fragen wir nach Ihrer Telefonnummer: Über diese Nummer werden Sie zu Kursen hinzugefügt.',
      fullNameLabel: 'Vor- und Nachname',
      enterFullName: 'Geben Sie Ihren vollständigen Namen ein.',
      fullNameTooLong: 'Ein Name darf höchstens {max} Zeichen haben.',
      passwordHint: 'Mindestens {min} Zeichen.',
      passwordLength: 'Ein Passwort muss {min}–{max} Zeichen lang sein.',
      confirmPasswordLabel: 'Passwort wiederholen',
      repeatPassword: 'Geben Sie das Passwort noch einmal ein.',
      passwordsDiffer: 'Die Passwörter stimmen nicht überein – bitte erneut eingeben.',
      moreOptions: 'Weitere Optionen',
      usernameOptionalLabel: 'Benutzername (optional)',
      usernameOptionalHint:
        'Falls Sie sich außer mit der Telefonnummer auch mit einem Benutzernamen anmelden möchten. {min}–{max} Zeichen: lateinische Buchstaben, Ziffern und . _ -',
      usernameIsPhone: 'Ein Benutzername darf keine Telefonnummer sein – mit Ihrer Nummer melden Sie sich ohnehin an.',
      usernameTaken: 'Dieser Benutzername ist vergeben. Bitte wählen Sie einen anderen.',
      createAccount: 'Konto anlegen',
      creating: 'Wird erstellt…',
      haveAccount: 'Sie haben schon ein Konto?',

      settingsTitle: 'Einstellungen',
      profileTitle: 'Ihr Konto',
      usernameNotSet: 'nicht festgelegt',
      profileReadOnly:
        'Diese Angaben lassen sich hier nicht ändern. Wenn etwas nicht stimmt, wenden Sie sich an Ihre Lehrkraft oder einen Administrator.',
      languageTitle: 'Sprache',
      languageHint: 'Ihre Wahl wird in Ihrem Konto gespeichert – auch auf Ihren anderen Geräten öffnet sich die Seite in dieser Sprache.',
      languageGroup: 'Sprache der Oberfläche',
      passwordTitle: 'Passwort ändern',
      otherDevicesTitle: 'Andere Geräte werden abgemeldet',
      otherDevicesBody:
        'Wenn Sie Ihr Passwort ändern, enden alle Sitzungen außer dieser: Auf Ihrem Telefon, einem anderen Computer und in anderen Browsern müssen Sie sich neu anmelden. Hat jemand anderes Ihr Passwort erfahren, sperrt genau das ihn aus Ihrem Konto aus.',
      currentPasswordLabel: 'Aktuelles Passwort',
      newPasswordLabel: 'Neues Passwort',
      repeatNewPasswordLabel: 'Neues Passwort wiederholen',
      enterCurrentPassword: 'Geben Sie Ihr aktuelles Passwort ein.',
      enterNewPassword: 'Geben Sie ein neues Passwort ein.',
      newPasswordLength: 'Das neue Passwort muss {min}–{max} Zeichen lang sein.',
      newSameAsCurrent: 'Das neue Passwort muss sich vom aktuellen unterscheiden.',
      repeatNewPassword: 'Geben Sie das neue Passwort noch einmal ein.',
      wrongCurrentPassword: 'Das aktuelle Passwort ist falsch.',
      checkCurrentPassword: 'Prüfen Sie Ihr aktuelles Passwort.',
      passwordAlreadyInUse: 'Das ist bereits Ihr Passwort. Bitte wählen Sie ein anderes.',
      passwordChanged:
        'Passwort geändert. Ihre Sitzungen auf anderen Geräten wurden beendet – melden Sie sich dort mit dem neuen Passwort wieder an.',

      choosePasswordTitle: 'Wählen Sie Ihr eigenes Passwort',
      choosePasswordLead:
        'Sie haben sich mit einem vorläufigen Passwort angemeldet. Eine Lehrkraft oder ein Administrator hat es Ihnen gegeben und kennt es daher ebenfalls. Bevor Sie zu Ihren Kursen gehen, wählen Sie ein neues Passwort, das nur Sie kennen.',
      issuedPasswordLabel: 'Das Passwort, das Sie erhalten haben',
      issuedPasswordHint: 'Das, mit dem Sie sich gerade angemeldet haben.',
      enterIssuedPassword: 'Geben Sie das Passwort ein, das Sie erhalten haben.',
      wrongIssuedPassword:
        'Das ist nicht das Passwort, das Sie erhalten haben. Geben Sie es genau so ein, wie es in der Nachricht steht.',
      rulesTitle: 'Das neue Passwort sollte:',
      ruleLength: 'mindestens {min} Zeichen lang sein (höchstens {max})',
      ruleDifferent: 'sich von dem Passwort unterscheiden, das Sie erhalten haben',
      ruleYours: 'keines sein, das Sie auf anderen Websites verwenden',
      newSameAsIssued: 'Das neue Passwort muss sich von dem unterscheiden, das Sie erhalten haben.',
      choosePasswordDevices: 'Sobald es gespeichert ist, enden Ihre Sitzungen auf anderen Geräten. Hier bleiben Sie angemeldet.',
      choosePasswordScope:
        'Hat Ihnen eine Lehrkraft oder ein Administrator dieses Passwort gegeben, gilt auch das Passwort, das Sie jetzt wählen, nur auf dieser Website. Ein Passwort, das auf allen MyMentor-Plattformen gilt, kann Ihnen nur der Betreiber von MyMentor geben – als neues vorläufiges Passwort; fragen Sie Ihren Administrator danach.',
      savePassword: 'Speichern und fortfahren',
      savingPassword: 'Wird gespeichert…',
      laterHint: 'Nicht jetzt? Sie können sich abmelden – diese Seite öffnet sich bei der nächsten Anmeldung wieder.',

      err_phone_taken:
        'Für diese Nummer gibt es bereits ein Konto. Melden Sie sich auf der Anmeldeseite mit Ihrer Telefonnummer an oder wenden Sie sich an Ihren Administrator.',
      err_invalid_locale: 'Diese Sprache wird nicht unterstützt.',
    },
  },
)
